import React, { useState, useEffect } from 'react';
import './TestimonialsSection.css';

interface Testimonial {
  id: string;
  name: string;
  title: string;
  company: string;
  avatar: string;
  quote: string;
  rating: number;
  metrics?: {
    improvement: string;
    timeframe: string;
  };
}

interface TestimonialsSectionProps {
  onContactSales?: () => void;
}

const TestimonialsSection: React.FC<TestimonialsSectionProps> = ({ onContactSales }) => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const testimonials: Testimonial[] = [
    {
      id: 'sarah-chen',
      name: 'Sarah Chen',
      title: 'VP of Supply Chain',
      company: 'TechFlow Logistics',
      avatar: '👩‍💼',
      quote: 'Optinode AI transformed our warehouse operations completely. We reduced inventory costs by 35% while improving delivery times. The AI-powered optimization is simply incredible.',
      rating: 5,
      metrics: {
        improvement: '35% cost reduction',
        timeframe: 'Within 3 months'
      }
    },
    {
      id: 'michael-rodriguez',
      name: 'Michael Rodriguez',
      title: 'Operations Director',
      company: 'Global Distribution Corp',
      avatar: '👨‍💼',
      quote: 'The real-time analytics and forecasting capabilities have given us unprecedented visibility into our supply chain. We can now predict and prevent stockouts before they happen.',
      rating: 5,
      metrics: {
        improvement: '60% fewer stockouts',
        timeframe: 'First quarter'
      }
    },
    {
      id: 'emily-watson',
      name: 'Emily Watson',
      title: 'Chief Technology Officer',
      company: 'Retail Innovations Inc',
      avatar: '👩‍💻',
      quote: 'Implementation was seamless and the ROI was immediate. The platform scales beautifully with our growing business and the support team is exceptional.',
      rating: 5,
      metrics: {
        improvement: '200% ROI',
        timeframe: 'Year one'
      }
    },
    {
      id: 'david-kim',
      name: 'David Kim',
      title: 'Logistics Manager',
      company: 'Pacific Supply Solutions',
      avatar: '👨‍🔧',
      quote: 'The automated optimization has freed up our team to focus on strategic initiatives. What used to take days of manual planning now happens in minutes.',
      rating: 5,
      metrics: {
        improvement: '80% time savings',
        timeframe: 'Daily operations'
      }
    }
  ];

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials.length]);

  const handleTestimonialClick = (index: number) => {
    setActiveTestimonial(index);
    setIsAutoPlaying(false);
    // Resume auto-play after 10 seconds
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={`star ${i < rating ? 'filled' : ''}`}>
        ⭐
      </span>
    ));
  };

  return (
    <section className="testimonials-section" aria-labelledby="testimonials-heading">
      <div className="testimonials-container">
        <div className="testimonials-header">
          <h2 id="testimonials-heading" className="testimonials-title">
            Trusted by Industry Leaders
          </h2>
          <p className="testimonials-subtitle">
            See how companies worldwide are transforming their supply chains with Optinode AI
          </p>
        </div>

        <div className="testimonials-content">
          <div className="testimonial-main">
            <div className="testimonial-card">
              <div className="testimonial-header">
                <div className="testimonial-avatar">
                  <span role="img" aria-label={`${testimonials[activeTestimonial].name} avatar`}>
                    {testimonials[activeTestimonial].avatar}
                  </span>
                </div>
                <div className="testimonial-info">
                  <h3 className="testimonial-name">{testimonials[activeTestimonial].name}</h3>
                  <p className="testimonial-title">{testimonials[activeTestimonial].title}</p>
                  <p className="testimonial-company">{testimonials[activeTestimonial].company}</p>
                </div>
                <div className="testimonial-rating">
                  {renderStars(testimonials[activeTestimonial].rating)}
                </div>
              </div>
              
              <blockquote className="testimonial-quote">
                "{testimonials[activeTestimonial].quote}"
              </blockquote>
              
              {testimonials[activeTestimonial].metrics && (
                <div className="testimonial-metrics">
                  <div className="metric">
                    <span className="metric-value">
                      {testimonials[activeTestimonial].metrics!.improvement}
                    </span>
                    <span className="metric-timeframe">
                      {testimonials[activeTestimonial].metrics!.timeframe}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="testimonials-navigation">
            <div className="testimonial-dots">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  className={`testimonial-dot ${index === activeTestimonial ? 'active' : ''}`}
                  onClick={() => handleTestimonialClick(index)}
                  aria-label={`View testimonial ${index + 1}`}
                />
              ))}
            </div>
            
            <div className="testimonial-thumbnails">
              {testimonials.map((testimonial, index) => (
                <button
                  key={testimonial.id}
                  className={`testimonial-thumbnail ${index === activeTestimonial ? 'active' : ''}`}
                  onClick={() => handleTestimonialClick(index)}
                  aria-label={`View testimonial from ${testimonial.name}`}
                >
                  <div className="thumbnail-avatar">
                    <span role="img" aria-label={`${testimonial.name} avatar`}>
                      {testimonial.avatar}
                    </span>
                  </div>
                  <div className="thumbnail-info">
                    <span className="thumbnail-name">{testimonial.name}</span>
                    <span className="thumbnail-company">{testimonial.company}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="testimonials-stats">
          <div className="stat-item">
            <span className="stat-number">500+</span>
            <span className="stat-label">Happy Customers</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">98%</span>
            <span className="stat-label">Satisfaction Rate</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">24/7</span>
            <span className="stat-label">Support Available</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">50+</span>
            <span className="stat-label">Countries Served</span>
          </div>
        </div>

        <div className="testimonials-cta">
          <h3 className="cta-title">Ready to Join Our Success Stories?</h3>
          <p className="cta-description">
            Let our team show you how Optinode AI can transform your supply chain operations.
          </p>
          <div className="cta-actions">
            <button 
              className="btn btn-primary btn-large"
              onClick={onContactSales}
            >
              Contact Sales
            </button>
            <button className="btn btn-secondary btn-large">
              View Case Studies
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;